import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, AlertCircle, Loader2, ArrowRight, LogIn, UserPlus } from 'lucide-react';
import { Button, Badge } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { classInviteService } from '../../services/classInviteService';
import { useAuth } from '../../contexts/AuthContext';
import type { ClassInvitePreviewDto } from '../../types/class';
import { setMyClassStatus } from '../../utils/myClassStatus';
import { invalidateEnrolledClassListQueries } from '../../data/queries/useClasses';
import { ApiError } from '../../services/apiClient';
import { formatLocaleDateShort } from '@/lib/localeFormat';

function isExpiredInvite(expiresAt: string | null): boolean {
	if (expiresAt == null || String(expiresAt).trim() === '') return false;
	const t = new Date(expiresAt).getTime();
	if (!Number.isFinite(t)) return false;
	return t < Date.now();
}

export default function ClassInviteAccept() {
	const { i18n } = useTranslation();
	const { token } = useParams<{ token: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user } = useAuth();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [preview, setPreview] = useState<ClassInvitePreviewDto | null>(null);
	const [processing, setProcessing] = useState(false);
	const [success, setSuccess] = useState(false);

	useEffect(() => {
		if (token) {
			void validateInvite(token);
		} else {
			setError('유효하지 않은 초대 링크입니다.');
			setLoading(false);
		}
	}, [token]);

	const validateInvite = async (inviteToken: string) => {
		try {
			const resolved = await classInviteService.getInviteByToken(inviteToken);
			if (resolved.preview.isAlreadyJoined) {
				setPreview(resolved.preview);
				setLoading(false);
				return;
			}
			if (isExpiredInvite(resolved.preview.expiresAt)) {
				setError('초대 링크가 만료되었습니다.');
				return;
			}
			setPreview(resolved.preview);
		} catch (err) {
			if (err instanceof ApiError) {
				setError(err.message);
			} else {
				setError('초대 확인 중 오류가 발생했습니다.');
			}
		} finally {
			setLoading(false);
		}
	};

	const handleAccept = async () => {
		if (!user) {
			localStorage.setItem('pending_invite_token', token || '');
			navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
			return;
		}

		if (preview?.isAlreadyJoined) {
			navigate(`/student/my-classes/${preview.classId}`);
			return;
		}

		setProcessing(true);
		try {
			const res = await classInviteService.acceptInvite(token!);
			if (res.success) {
				setSuccess(true);
				const classId = String(res.classId);
				if (classId) {
					setMyClassStatus(classId, 'participating');
				}
				void invalidateEnrolledClassListQueries(queryClient);
				setTimeout(() => {
					navigate(`/student/my-classes/${res.classId}`);
				}, 2000);
			} else {
				setError(res.message || '초대 수락에 실패했습니다. 이미 참여 중인 클래스일 수 있습니다.');
			}
		} catch (err) {
			if (err instanceof ApiError) {
				if (err.status === 422) {
					setError('참여 가능 인원이 초과되었습니다.');
				} else {
					setError(err.message);
				}
			} else {
				setError('초대 수락 중 오류가 발생했습니다.');
			}
		} finally {
			setProcessing(false);
		}
	};

	if (loading) {
		return (
			<div className="min-h-[60vh] flex flex-col items-center justify-center p-4">
				<Loader2 size={48} className="text-blue-500 animate-spin mb-4" />
				<p className="text-gray-600 font-medium">초대 정보를 확인하고 있습니다...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className="min-h-[60vh] flex flex-col items-center justify-center p-4">
				<div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-6">
					<AlertCircle size={32} className="text-red-500" />
				</div>
				<h2 className="text-2xl font-bold text-gray-900 mb-2">초대를 처리할 수 없습니다</h2>
				<p className="text-gray-500 mb-8 text-center max-w-md">{error}</p>
				<Button variant="outline" onClick={() => navigate(ROUTES.COMMON.CLASS_LIST)}>
					클래스 목록으로 돌아가기
				</Button>
			</div>
		);
	}

	if (success) {
		return (
			<div className="min-h-[60vh] flex flex-col items-center justify-center p-4 animate-in zoom-in duration-300">
				<div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6">
					<CheckCircle2 size={40} className="text-green-500" />
				</div>
				<h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">초대를 수락했습니다!</h2>
				<p className="text-gray-600 mb-4 text-center">
					{preview?.title || '클래스'}에 성공적으로 참여되었습니다.
				</p>
				<p className="text-sm text-gray-400">잠시 후 클래스 페이지로 이동합니다...</p>
			</div>
		);
	}

	const title = preview?.title || '클래스 참여 초대';
	const thumb = preview?.thumbnailImageUrl || undefined;
	const maxP = preview?.maxParticipants;
	const expiresLabel =
		preview?.expiresAt != null && String(preview.expiresAt).trim() !== ''
			? `${formatLocaleDateShort(preview.expiresAt, i18n.language)} 까지`
			: '별도 만료 없음';

	return (
		<div className="min-h-[70vh] flex items-center justify-center p-4">
			<div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
				<div className="relative h-48 bg-blue-600 overflow-hidden">
					{thumb ? (
						<img src={thumb} alt={title} className="w-full h-full object-cover opacity-80" />
					) : (
						<div className="w-full h-full flex items-center justify-center">
							<LinkGlyph size={64} className="text-blue-200 opacity-20" />
						</div>
					)}
					<div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
					<div className="absolute bottom-4 left-6 right-6">
						<Badge className="mb-2 bg-blue-500 border-none">클래스 초대</Badge>
						<h1 className="text-xl font-bold text-white leading-tight">{title}</h1>
					</div>
				</div>

				<div className="p-8 space-y-6">
					<div className="space-y-4">
						{preview?.isAlreadyJoined ? (
							<p className="text-gray-600 leading-relaxed text-center">
								이미 <strong>{title}</strong>에 참여 중입니다.
							</p>
						) : (
							<p className="text-gray-600 leading-relaxed text-center">
								사용자님을 <strong>{title}</strong>에 초대합니다.<br />
								지금 바로 참여하여 교육 과정을 시작해보세요.
							</p>
						)}

						<div className="bg-gray-50 rounded-lg p-4 space-y-2">
							<div className="flex justify-between text-sm">
								<span className="text-gray-500">초대 유효기간</span>
								<span className="text-gray-900 font-medium">{expiresLabel}</span>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-gray-500">참여자 수</span>
								<span className="text-gray-900 font-medium">
									{preview?.participantCount ?? 0} /{' '}
									{maxP != null && maxP > 0 ? maxP : '무제한'}
								</span>
							</div>
						</div>
					</div>

					<div className="space-y-3">
						<Button
							variant="primary"
							className="w-full h-12 text-base font-bold shadow-lg shadow-blue-100"
							onClick={handleAccept}
							disabled={processing}
						>
							{processing ? (
								<Loader2 size={20} className="animate-spin" />
							) : preview?.isAlreadyJoined ? (
								<>내 클래스로 이동 <ArrowRight size={18} className="ml-2" /></>
							) : user ? (
								<>초대 수락하고 참여하기 <ArrowRight size={18} className="ml-2" /></>
							) : (
								<>로그인하고 참여하기 <ArrowRight size={18} className="ml-2" /></>
							)}
						</Button>

						{!user && (
							<div className="flex flex-col items-center gap-3 pt-2">
								<p className="text-xs text-gray-400">계정이 없으신가요?</p>
								<div className="flex gap-4">
									<Link
										to="/signup"
										className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
									>
										<UserPlus size={14} /> 회원가입
									</Link>
									<div className="w-px h-3 bg-gray-200" />
									<Link
										to="/login"
										className="text-sm font-semibold text-gray-600 hover:text-gray-900 flex items-center gap-1"
									>
										<LogIn size={14} /> 로그인
									</Link>
								</div>
							</div>
						)}
					</div>
				</div>

				<div className="bg-gray-50 px-8 py-4 border-t border-gray-100">
					<p className="text-[11px] text-gray-400 text-center leading-normal">
						본 초대는 발신자에 의해 언제든지 취소될 수 있습니다.<br />
						도움이 필요하시면 고객센터로 문의해주세요.
					</p>
				</div>
			</div>
		</div>
	);
}

const LinkGlyph = ({ size, className }: { size: number; className: string }) => (
	<svg
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
		<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
	</svg>
);
